/**
 * Teams Bot Wizard — 4-step wizard to configure a Microsoft Teams Bot channel.
 *
 * Step 1: Create Azure Bot Service (instructional)
 * Step 2: Enter credentials (Bot App ID + App Secret)
 * Step 3: Configure Atlas channel (name, agent, settings)
 * Step 4: Finalise — copy webhook URL back to Azure Bot Service
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Copy, ExternalLink, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { z } from 'zod';
import { Button, Card, Input, Label } from '@/components/ui';
import { useAgents } from '@/features/agents/hooks/useAgents';
import { useHives } from '@/features/agents/hives';
import { useCreateChannel } from '@/features/channels';
import { toast } from 'sonner';

// =============================================================================
// Validation schemas
// =============================================================================

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const credentialsSchema = z.object({
  bot_app_id: z
    .string()
    .regex(GUID_REGEX, 'Must be a valid GUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)'),
  bot_app_secret: z.string().min(1, 'App secret is required'),
  teams_tenant_id: z
    .string()
    .regex(GUID_REGEX, 'Must be a valid Azure AD Tenant ID (GUID)'),
});

const channelConfigSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(80),
  target: z.string().min(1, 'Select an agent or hive'),
  session_ttl_hours: z.number().int().min(1).max(720).default(168),
  access_mode: z.enum(['public', 'allowlist', 'denylist']).default('allowlist'),
});

type CredentialsForm = z.infer<typeof credentialsSchema>;
type ChannelConfigForm = z.infer<typeof channelConfigSchema>;

// =============================================================================
// Step indicators
// =============================================================================

const STEPS = ['Azure Bot Service', 'Credentials', 'Configure', 'Finalise'] as const;

function StepIndicator({ current }: { current: number }) {
  return (
    <nav className="flex items-center justify-center gap-2 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      {STEPS.map((label, idx) => {
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={label} className="flex items-center">
            {idx > 0 && (
              <div className={`w-8 h-px mx-2 ${done ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium
                  ${done ? 'bg-blue-500 text-white' : active
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 ring-2 ring-blue-500'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}
              >
                {done ? <CheckCircle className="w-3.5 h-3.5" /> : idx + 1}
              </div>
              <span className={`text-xs hidden sm:inline ${active ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-500'}`}>
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </nav>
  );
}

// =============================================================================
// Step 1 — Create Azure Bot Service (informational)
// =============================================================================

function Step1() {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create Azure Bot Service</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Before configuring the channel in Klviar, you need to register your bot in Azure.
      </p>

      <Card className="p-4 space-y-3">
        {[
          {
            step: '1',
            text: 'Go to Azure Portal → Create a resource → Search for "Azure Bot"',
          },
          {
            step: '2',
            text: 'Fill in: Bot handle (unique name), Subscription, Resource Group, and Pricing tier (F0 for dev, S1 for prod).',
          },
          {
            step: '3',
            text: 'Under "Microsoft App ID", choose "Create new Microsoft App ID". Note down the App ID once created.',
          },
          {
            step: '4',
            text: 'After creation, go to the App Registration → Certificates & secrets → New client secret. Copy the secret value immediately.',
          },
          {
            step: '5',
            text: 'In your Azure Bot → Channels → Add Teams channel.',
          },
        ].map(({ step, text }) => (
          <div key={step} className="flex gap-3">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold shrink-0 mt-0.5">
              {step}
            </span>
            <p className="text-sm text-gray-700 dark:text-gray-300">{text}</p>
          </div>
        ))}
      </Card>

      <a
        href="https://learn.microsoft.com/en-us/azure/bot-service/bot-service-quickstart-registration"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Azure Bot Service documentation
      </a>
    </div>
  );
}

// =============================================================================
// Step 2 — Credentials
// =============================================================================

function Step2({ form }: { form: ReturnType<typeof useForm<CredentialsForm>> }) {
  const { register, formState: { errors } } = form;

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Enter Bot Credentials</h2>
      <p className="text-sm text-gray-500">
        These credentials are stored encrypted in Klviar and used to authenticate with the Bot Connector API.
      </p>

      <div className="space-y-4">
        <div>
          <Label htmlFor="bot_app_id">Microsoft App ID (Client ID)</Label>
          <Input
            id="bot_app_id"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            {...register('bot_app_id')}
            className="mt-1 font-mono text-sm"
          />
          {errors.bot_app_id && (
            <p className="mt-1 text-xs text-red-500">{errors.bot_app_id.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Found in Azure Portal → App Registration → Overview → Application (client) ID
          </p>
        </div>

        <div>
          <Label htmlFor="bot_app_secret">Microsoft App Password (Client Secret)</Label>
          <Input
            id="bot_app_secret"
            type="password"
            placeholder="Secret value from Azure App Registration"
            {...register('bot_app_secret')}
            className="mt-1"
          />
          {errors.bot_app_secret && (
            <p className="mt-1 text-xs text-red-500">{errors.bot_app_secret.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="teams_tenant_id">Azure AD Tenant ID</Label>
          <Input
            id="teams_tenant_id"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            {...register('teams_tenant_id')}
            className="mt-1 font-mono text-sm"
          />
          {errors.teams_tenant_id && (
            <p className="mt-1 text-xs text-red-500">{errors.teams_tenant_id.message}</p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Found in Azure Portal → Microsoft Entra ID → Overview → Tenant ID
          </p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Step 3 — Configure Atlas channel
// =============================================================================

function Step3({ form }: { form: ReturnType<typeof useForm<ChannelConfigForm>> }) {
  const { register, formState: { errors } } = form;
  const { data: agentsData } = useAgents();
  const { data: hivesData } = useHives();

  const agentOptions = (agentsData ?? []).map((a) => ({
    value: a.id,
    label: a.name,
  }));
  const hiveOptions = (hivesData?.data ?? []).map((h) => ({
    value: h.id,
    label: h.name,
  }));

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Configure Channel</h2>
      <p className="text-sm text-gray-500">
        Set up the Klviar channel integration settings.
      </p>

      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Channel Name</Label>
          <Input
            id="name"
            placeholder="e.g. IT Support Teams Bot"
            {...register('name')}
            className="mt-1"
          />
          {errors.name && (
            <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="target">Assigned Target</Label>
          <select
            id="target"
            {...register('target')}
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">— Select an agent or hive —</option>
            {agentOptions.length > 0 && (
              <optgroup label="Agents">
                {agentOptions.map((o) => (
                  <option key={`agent:${o.value}`} value={`agent:${o.value}`}>{o.label}</option>
                ))}
              </optgroup>
            )}
            {hiveOptions.length > 0 && (
              <optgroup label="Hives">
                {hiveOptions.map((o) => (
                  <option key={`hive:${o.value}`} value={`hive:${o.value}`}>{o.label}</option>
                ))}
              </optgroup>
            )}
          </select>
          {errors.target && (
            <p className="mt-1 text-xs text-red-500">{errors.target.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="access_mode">Access Mode</Label>
          <select
            id="access_mode"
            {...register('access_mode')}
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="allowlist">Allowlist (only invited users)</option>
            <option value="public">Public (all Teams users)</option>
            <option value="denylist">Denylist (all except blocked)</option>
          </select>
          <p className="mt-1 text-xs text-gray-400">
            Allowlist is recommended — only users in the access list can interact with the bot.
          </p>
        </div>

        <div>
          <Label htmlFor="session_ttl_hours">Session TTL (hours)</Label>
          <Input
            id="session_ttl_hours"
            type="number"
            min={1}
            max={720}
            {...register('session_ttl_hours', { valueAsNumber: true })}
            className="mt-1"
          />
          <p className="mt-1 text-xs text-gray-400">
            Inactivity window before a conversation is closed (1–720 hours).
          </p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Step 4 — Finalise
// =============================================================================

function Step4({ webhookUrl }: { webhookUrl: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Finalise in Azure Bot Service</h2>
      <p className="text-sm text-gray-500">
        Copy the webhook URL below and paste it into your Azure Bot Service as the Messaging Endpoint.
      </p>

      <Card className="p-4">
        <p className="text-xs font-medium text-gray-500 mb-2">Webhook URL</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-800 dark:text-gray-200 break-all">
            {webhookUrl || 'Creating channel…'}
          </code>
          <Button
            variant="ghost"
            size="sm"
            onClick={copy}
            disabled={!webhookUrl}
            title="Copy URL"
          >
            <Copy className={`w-4 h-4 ${copied ? 'text-green-500' : ''}`} />
          </Button>
        </div>
      </Card>

      <Card className="p-4 space-y-3">
        {[
          'Go to Azure Portal → your Azure Bot → Configuration',
          'Paste the webhook URL into the "Messaging endpoint" field',
          'Click Apply / Save',
          'Back in Teams, send a message to your bot to verify the connection',
        ].map((text, i) => (
          <div key={i} className="flex gap-3">
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs font-bold shrink-0 mt-0.5">
              {i + 1}
            </span>
            <p className="text-sm text-gray-700 dark:text-gray-300">{text}</p>
          </div>
        ))}
      </Card>

      <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800">
        <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
        <p className="text-xs text-green-700 dark:text-green-300">
          Channel created successfully. It will become active once Azure Bot Service is configured.
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Main Wizard
// =============================================================================

export function TeamsBotWizardPage() {
  const navigate = useNavigate();
  const createChannel = useCreateChannel();

  const [step, setStep] = useState(0);
  const [createdWebhookUrl, setCreatedWebhookUrl] = useState('');

  const credForm = useForm<CredentialsForm>({
    resolver: formResolver(credentialsSchema),
  });

  const configForm = useForm<ChannelConfigForm>({
    resolver: formResolver(channelConfigSchema),
    defaultValues: {
      session_ttl_hours: 168,
      access_mode: 'allowlist',
    },
  });

  const credValues = credForm.watch();
  const configValues = configForm.watch();

  const canAdvance = () => {
    if (step === 0) return true;
    if (step === 1) {
      return (
        GUID_REGEX.test(credValues.bot_app_id ?? '')
        && !!credValues.bot_app_secret
        && GUID_REGEX.test(credValues.teams_tenant_id ?? '')
      );
    }
    if (step === 2) {
      return !!configValues.name && configValues.name.length >= 2 && !!configValues.target;
    }
    return true;
  };

  const handleNext = async () => {
    if (step === 1) {
      const valid = await credForm.trigger();
      if (!valid) return;
    }

    if (step === 2) {
      const valid = await configForm.trigger();
      if (!valid) return;

      // Create channel on advance from step 3
      const creds = credForm.getValues();
      const config = configForm.getValues();
      const [targetType, targetId] = config.target.split(':', 2) as ['agent' | 'hive', string];

      try {
        const channel = await createChannel.mutateAsync({
          display_name: config.name,
          channel_type: 'teams',
          target_id: targetId,
          target_type: targetType,
          access_mode: config.access_mode,
          session_ttl_hours: config.session_ttl_hours,
          config: {
            bot_app_id: creds.bot_app_id,
            bot_app_secret: creds.bot_app_secret,
            teams_tenant_id: creds.teams_tenant_id,
          },
        });
        setCreatedWebhookUrl(channel.webhook_url);
      } catch {
        toast.error('Failed to create channel. Check your credentials and try again.');
        return;
      }
    }

    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    if (step === 0) {
      navigate('/channels');
      return;
    }
    setStep((s) => s - 1);
  };

  return (
    <div className="flex flex-col h-full min-h-0 max-w-2xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h1 className="text-base font-semibold text-gray-900 dark:text-white">
          Add Microsoft Teams Bot
        </h1>
        <Button variant="ghost" size="sm" onClick={() => navigate('/channels')}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Step indicator */}
      <StepIndicator current={step} />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {step === 0 && <Step1 />}
        {step === 1 && <Step2 form={credForm} />}
        {step === 2 && <Step3 form={configForm} />}
        {step === 3 && <Step4 webhookUrl={createdWebhookUrl} />}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>

        {step < STEPS.length - 1 ? (
          <Button
            variant="default"
            size="sm"
            onClick={handleNext}
            disabled={!canAdvance() || createChannel.isPending}
          >
            {createChannel.isPending ? 'Creating…' : 'Next'}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button variant="default" size="sm" onClick={() => navigate('/channels')}>
            Done
          </Button>
        )}
      </div>
    </div>
  );
}
